import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";
import { DrawCabinetFrontElementGroup } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElementGroup";
import { DrawCabinetInsideElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/DrawCabinetInsideElement";
import { DrawCabinetInsideGroupElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/DrawCabinetInsideGroupElement";
import { IDrawCabinetInsideElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/IDrawCabinetInsideElement";
import { DrawConstant } from "$src/CGS/domain/dto/constant/DrawConstant";
import { EFrontElementExtension } from "$src/CGS/domain/dto/core/EFrontElement";
import { EInsideElement } from "$src/CGS/domain/dto/core/EInsideElement";
import { EOrientation } from "$src/CGS/domain/dto/core/EOrientation";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { ApplianceApertureIdentifier } from "$src/CGS/domain/dto/core/bones/ApplianceApertureIdentifier";
import { EBoneType } from "$src/CGS/domain/dto/core/bones/EBoneType";
import { ICabinetShapeGenerator } from "../../../ShapeGenerators/ICabinetShapeGenerator";
import { FrontElementComputation } from "../FrontElementComputation";
import { FrontElementGroupComputation } from "../FrontElementGroupComputation";
import { InsideApplianceComputation } from "../InsideApplianceComputation";
import { InsideComputation } from "../InsideComputation";
import { InsideElementComputation } from "../InsideElementComputation";
import { InsideGroupComputation } from "../InsideGroupComputation";

export class ComputationElementBuilder {
    private readonly _constants: DrawConstant;
    private readonly _cabinetShapeGenerator: ICabinetShapeGenerator;

    constructor(constants: DrawConstant, cabinetShapeGenerator: ICabinetShapeGenerator) {
        this._constants = constants;
        this._cabinetShapeGenerator = cabinetShapeGenerator;
    }

    build(element: DrawCabinetFrontElement, isWithGap: boolean, hasElementAbove = false, hasElementBelow = false, aboveElemement: DrawCabinetFrontElement | undefined = undefined, belowElement: DrawCabinetFrontElement | undefined = undefined): FrontElementComputation {
        if (element instanceof DrawCabinetFrontElementGroup) {
            return new FrontElementGroupComputation(
                this._constants,
                element,
                element.Orientation,
                element.Elements.map((e, i) => {
                    const currentHasElementBelow = hasElementBelow || (element.Orientation === EOrientation.Vertical && i > 0);
                    const currentHasElementAbove = hasElementAbove || (element.Orientation === EOrientation.Vertical && i < element.Elements.length - 1);
                    let currentBelowElement = undefined;
                    let currentAboveElement = undefined;

                    if (element.Orientation === EOrientation.Vertical) {
                        if (i > 0)
                            currentBelowElement = element.Elements[i - 1];
                        if (i < element.Elements.length - 1)
                            currentAboveElement = element.Elements[i + 1];
                    }

                    return this.build(e, isWithGap, currentHasElementAbove, currentHasElementBelow, currentAboveElement, currentBelowElement);
                }),
                belowElement,
                aboveElemement,
                this.buildInside(element.Inside, element),
                hasElementAbove,
                hasElementBelow,
                isWithGap,
            );
        } else {
            if (!element.Width)
                throw new Error(`The leaf element ${ EFrontElementExtension.getName(element.FrontElement)} cannot have a null width value.`);

            return new FrontElementComputation(
                this._constants,
                element,
                belowElement,
                aboveElemement,
                this.buildInside(element.Inside, element),
                hasElementAbove,
                hasElementBelow,
                isWithGap,
            );
        }
    }

    private _insideApplianceIndices: Map<EInsideElement, ApplianceApertureIdentifier> = new Map([
        [EInsideElement.A, new ApplianceApertureIdentifier(EBoneType.Oven, 0)],
        [EInsideElement.DW, new ApplianceApertureIdentifier(EBoneType.Dishwasher, 0)],
        [EInsideElement.F, new ApplianceApertureIdentifier(EBoneType.Fridge, 0)],
        [EInsideElement.W, new ApplianceApertureIdentifier(EBoneType.WashingMachine, 0)]
    ]);

    buildInside(inside: IDrawCabinetInsideElement | undefined, parent: DrawCabinetFrontElement | DrawCabinetInsideGroupElement): InsideComputation | undefined {
        if (!inside)
            return undefined;

        if (inside instanceof DrawCabinetInsideElement) {
            const identifier = this._insideApplianceIndices.get(inside.Type);
            if (identifier) {
                let fixedHeight = this._cabinetShapeGenerator.Parameters.getInsideApplianceHeight(identifier);
                if (!fixedHeight && parent instanceof DrawCabinetFrontElement && inside.Height.isAuto)
                    fixedHeight = LengthMaths.getLengthValue(parent.Height);

                const computation = new InsideApplianceComputation(inside);
                computation.fixedHeight = fixedHeight;
                computation.applianceApertureIdentifier = identifier;

                // Increment index of all appliance types to ensure compatibility with FO
                this._insideApplianceIndices.forEach((value, key) => {
                    value.index++;
                    this._insideApplianceIndices.set(key, value);
                });

                return computation;
            }

            return new InsideElementComputation(
                inside
            );
        } else if (inside instanceof DrawCabinetInsideGroupElement) {
            const subElements = inside.SubElements
                .map(e => this.buildInside(e, inside))
                .filter((element): element is InsideComputation => element !== undefined);;

            return new InsideGroupComputation(
                inside.Orientation,
                subElements,
                inside
            );
        }

        throw new Error(`Unknown inside type: ${inside.constructor.name}`);
    }
}
